import { ShiftInviteStatus } from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import { alpha, Button, Chip, Stack } from "@mui/material";
import { useDeleteShiftInvite } from "@src/appV2/ShiftInvites/api/useDeleteShiftInvite";
import { PACIFIC_TIME_ZONE } from "@src/appV2/Timezone/constants";
import { isPast, isSameDay } from "date-fns";
// eslint-disable-next-line no-restricted-imports
import { format, utcToZonedTime } from "date-fns-tz";

import { formatDurationBetweenDates } from "../lib/Time/formatDurationBetweenDates";
import { useGetShiftInvite } from "../ShiftInvites/api/useGetShiftInvite";
import { ChatWidgetCard } from "./ChatWidgetCard";
import { SHIFT_INVITE_STATUS_CHIP_MAP } from "./utils";

interface ShiftInviteRequestMessageProps {
  shiftInviteId: string;
  message: string;
  status: ShiftInviteStatus;
}

interface ShiftInviteRequestMessageContentProps {
  shiftInviteId: string;
}

export function ShiftInviteRequestMessage(props: ShiftInviteRequestMessageProps) {
  const { shiftInviteId, status, message } = props;

  if (!isDefined(message) || status === ShiftInviteStatus.DELETED || !isDefined(status)) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <></>;
  }

  return <ShiftInviteRequestMessageContent shiftInviteId={shiftInviteId} />;
}

export function ShiftInviteRequestMessageContent(props: ShiftInviteRequestMessageContentProps) {
  const { shiftInviteId } = props;

  const { data: shiftInviteResponse } = useGetShiftInvite(
    { shiftInviteId },
    { enabled: isDefined(shiftInviteId) }
  );

  const { mutateAsync: deleteShiftInviteMutate, isLoading: isDeletingShiftInvite } =
    useDeleteShiftInvite();

  const shiftInvite = shiftInviteResponse?.data;

  if (!isDefined(shiftInvite)) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <></>;
  }

  const { status, expiresAt, shiftDetails, facility } = shiftInvite.attributes;

  const isExpired = status === ShiftInviteStatus.EXPIRED || isPast(new Date(expiresAt));
  const displayDuration = formatDurationBetweenDates({
    fromDate: new Date(),
    toDate: new Date(expiresAt),
    padMinutes: false,
  });

  const timezone = facility.tmz ?? PACIFIC_TIME_ZONE;
  const shiftStart = utcToZonedTime(shiftDetails.start, timezone);
  const shiftEnd = utcToZonedTime(shiftDetails.end, timezone);

  const formattedShiftStartDay = format(shiftStart, "MMM dd");
  const formattedShiftStartTime = format(shiftStart, "hh:mm a");
  const formattedShiftEndTime = format(shiftEnd, "hh:mm a");

  const formattedShiftTime = `${formattedShiftStartDay}, ${formattedShiftStartTime} - ${formattedShiftEndTime} ${
    isSameDay(shiftStart, shiftEnd) ? "" : " (next day)"
  }`;

  const isActionable = status === ShiftInviteStatus.PENDING && !isExpired;

  const chipProps = isExpired
    ? SHIFT_INVITE_STATUS_CHIP_MAP[ShiftInviteStatus.EXPIRED]
    : SHIFT_INVITE_STATUS_CHIP_MAP[status];

  const StatusChip = (
    <Chip
      label={chipProps.label}
      aria-label={chipProps.label}
      color={chipProps.color}
      variant="outlined"
      sx={{
        border: "none",
        borderRadius: 2,
        fontWeight: "bold",
        ...chipProps.customStyles,
      }}
    />
  );

  const handleRemove = async () => {
    await deleteShiftInviteMutate(shiftInviteId);
  };

  const removeButton = isActionable ? (
    <Button
      size="small"
      variant="outlined"
      sx={(theme) => ({
        width: "100%",
        borderColor: alpha(theme.palette.text.primary, 0.4),
        color: theme.palette.text.primary,
      })}
      disabled={isDeletingShiftInvite}
      onClick={handleRemove}
    >
      Remove
    </Button>
  ) : null;

  return (
    <Stack alignItems="flex-end" className="sendbird-admin-message">
      <ChatWidgetCard
        statusChip={StatusChip}
        title="You sent a shift invite"
        subtitle={formattedShiftTime}
        actions={removeButton}
        caption={isActionable ? `Expiring in ${displayDuration}` : undefined}
      />
    </Stack>
  );
}
